import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

// The PDF has an offset: PDF page N contains book page N-1
// So to show book page X, we need PDF page X+1
const BOOK_PAGE_OFFSET = 1;

const Reader = () => {
  const { user, token } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [chapters, setChapters] = useState([]);
  const [showChapters, setShowChapters] = useState(false);
  // currentPage stores the BOOK page number (what user sees printed in the book)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBookPages, setTotalBookPages] = useState(null);
  const [scale, setScale] = useState(1.5);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isRenderingRef = useRef(false);
  const navigate = useNavigate();

  // Check access on mount
  useEffect(() => {
    checkAccess();
    loadChapters();
  }, []);

  // Load PDF.js and PDF after access is confirmed
  useEffect(() => {
    if (hasAccess && token) {
      loadPdfJs();
    }
  }, [hasAccess, token]);

  // Render page when pdf, page, or scale changes
  useEffect(() => {
    if (pdfDoc && currentPage && !pdfLoading) {
      renderPage(currentPage);
    }
  }, [pdfDoc, currentPage, scale, pdfLoading]);

  // Fullscreen listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        goToPage(currentPage + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        goToPage(currentPage - 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, totalBookPages]);

  // Smooth scrolling - allow normal scroll, change page at boundaries
  useEffect(() => {
    const container = document.querySelector('.pdf-scroll-container');
    if (!container || !totalBookPages) return;

    let accumulatedDelta = 0;
    const pageChangeThreshold = 500; // Amount of scroll needed to change page

    const handleWheel = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const atTop = scrollTop <= 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 5;

      // If we can scroll within the container, let it scroll naturally
      if (!atTop && !atBottom) {
        return; // Allow default scroll
      }

      // At boundaries, accumulate scroll to change pages
      if ((atBottom && e.deltaY > 0) || (atTop && e.deltaY < 0)) {
        e.preventDefault();
        accumulatedDelta += e.deltaY;

        if (accumulatedDelta > pageChangeThreshold && currentPage < totalBookPages) {
          setCurrentPage(prev => Math.min(prev + 1, totalBookPages));
          accumulatedDelta = 0;
          // Scroll to top of new page
          container.scrollTop = 0;
        } else if (accumulatedDelta < -pageChangeThreshold && currentPage > 1) {
          setCurrentPage(prev => Math.max(prev - 1, 1));
          accumulatedDelta = 0;
          // Scroll to bottom of new page
          setTimeout(() => {
            container.scrollTop = container.scrollHeight;
          }, 100);
        }
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [currentPage, totalBookPages]);

  const loadPdfJs = () => {
    // Check if already loaded
    if (window.pdfjsLib) {
      loadPdf();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      loadPdf();
    };
    script.onerror = () => {
      toast.error('Kon nie PDF leser laai nie');
      setPdfLoading(false);
    };
    document.body.appendChild(script);
  };

  const loadPdf = async () => {
    setPdfLoading(true);
    try {
      // Fetch PDF as ArrayBuffer with auth header
      const response = await fetch(`${API}/book/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Kon nie PDF aflaai nie');
      }

      const arrayBuffer = await response.arrayBuffer();
      
      // Load PDF from ArrayBuffer
      const loadingTask = window.pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      
      setPdfDoc(pdf);
      // Total book pages = PDF pages - offset (since first PDF page is before book page 1)
      const bookPages = pdf.numPages - BOOK_PAGE_OFFSET;
      setTotalBookPages(bookPages);
      
      // Load saved progress
      await loadProgress(bookPages);
      setPdfLoading(false);
    } catch (error) {
      console.error('Error loading PDF:', error);
      toast.error('Kon nie PDF laai nie. Probeer asseblief weer.');
      setPdfLoading(false);
    }
  };

  const renderPage = useCallback(async (bookPageNum) => {
    if (!pdfDoc || !canvasRef.current || isRenderingRef.current) return;

    // Cancel any ongoing render
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch (e) {
        // Ignore cancel errors
      }
    }

    isRenderingRef.current = true;

    try {
      // Convert book page to PDF page (add offset)
      const pdfPageNum = bookPageNum + BOOK_PAGE_OFFSET;
      const page = await pdfDoc.getPage(pdfPageNum);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Set canvas dimensions
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Clear canvas
      context.clearRect(0, 0, canvas.width, canvas.height);

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      renderTaskRef.current = page.render(renderContext);
      await renderTaskRef.current.promise;
      
      // Save progress (book page number)
      saveProgress(bookPageNum);
    } catch (error) {
      if (error.name !== 'RenderingCancelledException') {
        console.error('Error rendering page:', error);
      }
    } finally {
      isRenderingRef.current = false;
      renderTaskRef.current = null;
    }
  }, [pdfDoc, scale]);

  const checkAccess = async () => {
    try {
      const response = await axios.get(`${API}/book/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasAccess(response.data.has_access);
      if (!response.data.has_access) {
        toast.error('U het nie toegang tot die boek nie. Koop dit asseblief eers.');
        navigate('/purchase');
      }
    } catch (error) {
      toast.error('Kon nie toegang verifieer nie');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async (totalPages) => {
    try {
      const response = await axios.get(`${API}/reading/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const savedPage = response.data.page_number || 1;
      setCurrentPage(Math.min(Math.max(1, savedPage), totalPages));
    } catch (error) {
      console.error('Failed to load progress:', error);
      setCurrentPage(1);
    }
  };

  const loadChapters = async () => {
    try {
      const response = await axios.get(`${API}/book/chapters`);
      setChapters(response.data.chapters || []);
    } catch (error) {
      console.error('Failed to load chapters:', error);
    }
  };

  const saveProgress = async (page) => {
    try {
      await axios.post(
        `${API}/reading/progress`,
        { page_number: page },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      // Silent fail for progress saving
    }
  };

  const goToPage = useCallback((page) => {
    if (totalBookPages && page >= 1 && page <= totalBookPages) {
      setCurrentPage(page);
    }
  }, [totalBookPages]);

  const goToChapter = (startPage) => {
    // Chapter start_page is already the book page number
    setCurrentPage(startPage);
    // Keep chapters sidebar open - user must close it manually
  };

  const toggleFullscreen = async () => {
    const container = containerRef.current;
    if (!container) return;

    try {
      if (!document.fullscreenElement) {
        await container.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
      toast.error('Kon nie volskerm aktiveer nie');
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-parchment">
        <p className="font-body text-xl text-muted-charcoal">Besig om te laai...</p>
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return (
    <div ref={containerRef} className="min-h-screen bg-parchment">
      {/* Reading Controls */}
      <div className="sticky top-0 z-50 bg-charcoal/95 backdrop-blur-sm border-b border-charcoal/20 py-4 px-6">
        <div className="container mx-auto max-w-7xl flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard"
              className="text-parchment hover:text-gold font-ui text-xs uppercase tracking-widest transition-colors"
            >
              ← Terug
            </button>
            <button
              onClick={() => setShowChapters(!showChapters)}
              data-testid="toggle-chapters"
              className="text-parchment hover:text-gold font-ui text-xs uppercase tracking-widest transition-colors"
            >
              {showChapters ? 'Versteek' : 'Wys'} Hoofstukke
            </button>
            <button
              onClick={toggleFullscreen}
              data-testid="toggle-fullscreen"
              className="text-parchment hover:text-gold font-ui text-xs uppercase tracking-widest transition-colors"
            >
              {isFullscreen ? '⊗ Verlaat Volskerm' : '⛶ Volskerm'}
            </button>
          </div>

          <div className="flex items-center gap-6">
            <span className="text-parchment font-ui text-xs">
              Bladsy {currentPage} / {totalBookPages || '...'}
            </span>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setScale(Math.max(0.8, scale - 0.2))}
                data-testid="zoom-out"
                className="text-parchment hover:text-gold font-ui text-xs transition-colors px-2"
              >
                A-
              </button>
              <button
                onClick={() => setScale(1.5)}
                data-testid="zoom-reset"
                className="text-parchment hover:text-gold font-ui text-xs transition-colors px-2"
              >
                100%
              </button>
              <button
                onClick={() => setScale(Math.min(2.5, scale + 0.2))}
                data-testid="zoom-in"
                className="text-parchment hover:text-gold font-ui text-xs transition-colors px-2"
              >
                A+
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Chapters Sidebar */}
      {showChapters && (
        <div className="fixed left-0 top-[73px] bottom-0 w-80 bg-charcoal/95 backdrop-blur-sm border-r border-gold/20 z-40 overflow-y-auto">
          <div className="p-6">
            <h2 className="font-heading text-2xl text-gold mb-6">Hoofstukke</h2>
            <div className="space-y-2">
              {chapters.map((chapter) => (
                <button
                  key={chapter.number}
                  onClick={() => goToChapter(chapter.start_page)}
                  data-testid={`chapter-${chapter.number}`}
                  className={`w-full text-left p-3 rounded-sm transition-colors ${
                    currentPage >= chapter.start_page &&
                    (chapters[chapter.number] ? currentPage < chapters[chapter.number].start_page : true)
                      ? 'bg-gold/20 text-gold'
                      : 'bg-parchment/5 hover:bg-parchment/10 text-parchment'
                  }`}
                >
                  <div className="font-ui text-xs uppercase tracking-widest opacity-60">
                    Hoofstuk {chapter.number}
                  </div>
                  <div className="font-body text-sm mt-1">{chapter.title}</div>
                  <div className="font-ui text-xs opacity-40 mt-1">Bladsy {chapter.start_page}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Watermark Overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-10 select-none"
        style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 200px, rgba(197, 160, 89, 0.015) 200px, rgba(197, 160, 89, 0.015) 400px)`,
        }}
      >
        <div className="absolute inset-0 flex flex-wrap items-center justify-center opacity-[0.03] text-xs text-gold font-ui rotate-[-30deg] select-none">
          {Array.from({ length: 30 }).map((_, i) => (
            <span key={i} className="px-8 py-4 whitespace-nowrap select-none">
              {user?.email} • {user?.user_id?.slice(0, 8)}
            </span>
          ))}
        </div>
      </div>

      {/* PDF Canvas */}
      <div
        className={`py-8 ${showChapters ? 'pl-80' : ''}`}
        onContextMenu={handleContextMenu}
        style={{ userSelect: 'none' }}
      >
        <div className="container mx-auto flex flex-col items-center">
          <div className="mb-4 text-center">
            <p className="font-body text-sm text-muted-charcoal">
              Gebruik pyltjies ↑↓ of muis wiel om te blaai
            </p>
          </div>

          {pdfLoading ? (
            <div className="flex items-center justify-center h-96">
              <p className="font-body text-lg text-muted-charcoal">PDF laai...</p>
            </div>
          ) : (
            <div 
              className="pdf-scroll-container overflow-y-auto max-h-[75vh] scrollbar-thin"
              style={{ scrollBehavior: 'smooth' }}
            >
              <canvas
                ref={canvasRef}
                data-testid="pdf-canvas"
                className="shadow-2xl max-w-full cursor-pointer"
                style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none' }}
              />
            </div>
          )}

          <div className="mt-8 flex justify-center items-center gap-4">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1 || pdfLoading}
              data-testid="prev-page"
              className="border border-charcoal hover:bg-charcoal hover:text-parchment text-charcoal rounded-sm px-6 py-3 font-ui uppercase tracking-widest text-xs transition-colors duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ← Vorige
            </button>

            <div className="flex items-center gap-2">
              <input
                type="number"
                value={currentPage}
                onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
                data-testid="page-input"
                className="w-16 bg-transparent border-b border-charcoal text-center py-2 outline-none text-charcoal"
                min="1"
                max={totalBookPages || 1}
              />
              <span className="font-ui text-sm text-muted-charcoal">/ {totalBookPages || '...'}</span>
            </div>

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalBookPages || pdfLoading}
              data-testid="next-page"
              className="border border-charcoal hover:bg-charcoal hover:text-parchment text-charcoal rounded-sm px-6 py-3 font-ui uppercase tracking-widest text-xs transition-colors duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Volgende →
            </button>
          </div>
        </div>
      </div>

      <style>{`
        canvas {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
        
        canvas::selection {
          background: transparent;
        }
        
        canvas::-moz-selection {
          background: transparent;
        }
      `}</style>
    </div>
  );
};

export default Reader;
